import React, { useEffect, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { BulkModal as EmployeeBulkModal } from '../usermgmt/BulkRegisterModal';
import { useBulkRegister } from '../usermgmt/useBulkRegister';
import { useDepartmentSettings } from '../usermgmt/useDepartmentSettings';
import {
  ATTENDANCE_TIME_TYPE_FALLBACK,
  normalizeAttendanceTimeType,
} from '../usermgmt/utils';
import { SALES_FIXED_DEPARTMENT } from '../usermgmt/utils';
import { tabGroupStyle, tabBtn } from '../usermgmt/styles';
import {
  actionRowStyle,
  badgeStyle,
  cardHeadStyle,
  cardStyle,
  cardSubStyle,
  cardTitleStyle,
  primaryButtonStyle,
} from './styles';

function getCompanyCode(company) {
  return String(company?.code || '').toUpperCase(); // 'PINE' 기본값 없음(2026-10-27) — 부르는 쪽이 늘 회사를 준다
}

// isHq: 이 회사가 이 사이트 본사인가(부모 CompanyInitialSetupSection → useHqCompany, 장부 기준 — 'PINE' 글자 판정 없앰 2026-10-27)
export default function EmployeeBulkSetup({ company, isHq = false }) {
  const { showToast } = useToast();
  const companyCode = getCompanyCode(company);
  const isSales = company?.target_type === 'group';
  const apiBase = isSales
    ? '/api/admin/sales-people'
    : (isHq ? '/api/admin/users' : `/api/admin/ext/${companyCode}/users`);
  const settingsPath = isSales
    ? null
    : (isHq
    ? '/api/admin/attendance-time-settings'
    : `/api/admin/ext/${companyCode}/attendance-time-settings`);
  const [attendanceTypeOptions, setAttendanceTypeOptions] = useState(ATTENDANCE_TIME_TYPE_FALLBACK);

  const departmentSettings = useDepartmentSettings({
    companyCode,
    deptType: isHq ? 'internal' : 'external',
    showToast,
  });

  const departments = isSales
    ? (departmentSettings.departmentNames.length > 0 ? departmentSettings.departmentNames : [SALES_FIXED_DEPARTMENT])
    : departmentSettings.departmentNames;

  useEffect(() => {
    if (!settingsPath) {
      setAttendanceTypeOptions(ATTENDANCE_TIME_TYPE_FALLBACK);
      return () => {};
    }
    let active = true;
    apiClient.get(settingsPath)
      .then((result) => {
        if (!active || !result.ok) return;
        const templates = Array.isArray(result.data?.data?.templates)
          ? result.data.data.templates
          : [];
        const names = templates
          .map((row) => String(row?.name || '').trim())
          .filter((name, index, arr) => Boolean(name) && arr.indexOf(name) === index);
        setAttendanceTypeOptions(names.length > 0 ? names : ATTENDANCE_TIME_TYPE_FALLBACK);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [settingsPath]);

  const getDefaultAttendanceType = useMemo(
    () => () => normalizeAttendanceTimeType('', attendanceTypeOptions),
    [attendanceTypeOptions],
  );

  const bulkHook = useBulkRegister({
    showToast,
    departments,
    getDefaultAttendanceType,
    fetchUsers: () => {},
    apiBase,
    fixedDepartment: isSales ? SALES_FIXED_DEPARTMENT : '',
  });

  // 마케터도 같은 '직원 일괄등록' 창에서(2026-10-26 사용자 결정 a — 창 위 [직원 | 마케터], 한 번에 한 종류).
  //   마케터 = 이 회사 소속 영업자(부서 '마케팅' 고정) → 이 회사 직원 관리 마케터 탭에 바로 보인다.
  //   회사는 주소에 싣는다(운영자가 위에서 고른 작업 대상과 상관없이 이 카드의 회사로). 직원 관리 마케터 탭의 [일괄등록]은 없앰.
  //   2026-11-03 합치기-2(결정 8a): 이름표만 [4대보험 | 3.3%]. 3.3% 일괄등록은 지금처럼 부서 '마케팅' 고정(안쪽 값 'sales' 그대로).
  const [kind, setKind] = useState('employee');
  const salesHook = useBulkRegister({
    showToast,
    departments: [SALES_FIXED_DEPARTMENT],
    getDefaultAttendanceType,
    fetchUsers: () => {},
    apiBase: '/api/admin/sales-people',
    bulkUrl: `/api/admin/sales-people/bulk?company_code=${encodeURIComponent(companyCode)}`,
    fixedDepartment: SALES_FIXED_DEPARTMENT,
  });
  const canPickKind = !isSales; // 옛 마케팅명부(그룹) 카드는 원래 마케터 전용
  const salesActive = canPickKind && kind === 'sales';
  const activeHook = salesActive ? salesHook : bulkHook;
  const openBulk = () => { setKind('employee'); salesHook.setBulkOpen(false); bulkHook.setBulkOpen(true); };
  const switchKind = (next) => {
    if (next === kind) return;
    (next === 'sales' ? salesHook : bulkHook).setBulkOpen(true);
    (next === 'sales' ? bulkHook : salesHook).setBulkOpen(false);
    setKind(next);
  };
  const kindPicker = canPickKind ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={tabGroupStyle}>
        <button type="button" style={tabBtn(kind === 'employee')} onClick={() => switchKind('employee')}>4대보험</button>
        <button type="button" style={tabBtn(kind === 'sales')} onClick={() => switchKind('sales')}>3.3%</button>
      </div>
      {salesActive && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>3.3% 일괄등록은 부서가 '마케팅'으로 정해져요. 다른 부서는 등록 뒤 직원 관리에서 바꿔 주세요.</span>}
    </div>
  ) : null;

  return (
    <section style={cardStyle}>
      <div style={cardHeadStyle}>
        <h3 style={cardTitleStyle}>직원 초기 등록</h3>
        <span style={badgeStyle}>{companyCode}</span>
      </div>
      <p style={cardSubStyle}>{canPickKind ? '4대보험·3.3% 인원 목록을 한 번에 붙여넣어 등록합니다(창 위에서 4대보험/3.3%를 골라요).' : '직원 목록을 한 번에 붙여넣어 등록합니다.'}</p>
      <div style={actionRowStyle}>
        <button style={primaryButtonStyle} onClick={openBulk}>
          <Users size={16} />
          직원 일괄등록
        </button>
      </div>
      <EmployeeBulkModal bulkHook={activeHook} departments={salesActive ? [SALES_FIXED_DEPARTMENT] : departments} headerExtra={kindPicker} />
    </section>
  );
}
